import { area, curveMonotoneX, line, scaleLinear } from 'd3';

export interface SparklineProps {
  values: readonly number[];
  ariaLabel: string;
  color?: string | undefined;
  area?: boolean | undefined;
  width?: number | undefined;
  height?: number | undefined;
  strokeWidth?: number | undefined;
  className?: string | undefined;
}

const defaultWidth = 120;
const defaultHeight = 32;

export function Sparkline({
  values,
  ariaLabel,
  color = 'var(--chart-1)',
  area: withArea = false,
  width = defaultWidth,
  height = defaultHeight,
  strokeWidth = 1.5,
  className,
}: SparklineProps) {
  const points = values
    .map((value, index) => ({ x: index, y: value }))
    .filter((point) => Number.isFinite(point.y));
  const drawable = points.length >= 2;
  const xScale = scaleLinear([0, Math.max(values.length - 1, 1)], [1, width - 1]);
  const minimum = Math.min(...points.map((point) => point.y));
  const maximum = Math.max(...points.map((point) => point.y));
  const padding = Math.max((maximum - minimum) * 0.1, Math.abs(maximum) * 0.05, Number.EPSILON);
  const yScale = scaleLinear([minimum - padding, maximum + padding], [height - 1, 1]);
  const lineGenerator = line<{ x: number; y: number }>()
    .x((point) => xScale(point.x))
    .y((point) => yScale(point.y))
    .curve(curveMonotoneX);
  const areaGenerator = area<{ x: number; y: number }>()
    .x((point) => xScale(point.x))
    .y0(height - 1)
    .y1((point) => yScale(point.y))
    .curve(curveMonotoneX);
  const accessibilityProps = drawable
    ? { role: 'img' as const, 'aria-label': ariaLabel }
    : { 'aria-hidden': 'true' as const };

  return (
    <svg
      className={className}
      width={width}
      height={height}
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      preserveAspectRatio="none"
      {...accessibilityProps}
    >
      {drawable && withArea ? (
        <path
          data-sparkline-area
          d={areaGenerator(points) ?? undefined}
          fill={color}
          fillOpacity={0.12}
        />
      ) : null}
      {drawable ? (
        <path
          data-sparkline-line
          d={lineGenerator(points) ?? undefined}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
    </svg>
  );
}
